import React from 'react';
import { Link } from 'react-router-dom';
import {
  calculateBcPropertyTransferTax,
  netBcPtt,
  BC_FTHB_FLAT_EXEMPTION,
} from '../../../utils/bcPropertyTransferTax';
import { formatUsd } from '../../../utils/articleScenarioMetrics';

const VANCOUVER_PRICE = 850_000;
const VICTORIA_PRICE = 650_000;
const vancouverPttGross = calculateBcPropertyTransferTax(VANCOUVER_PRICE);
const vancouverPttFthb = netBcPtt(VANCOUVER_PRICE, true);
const victoriaPttGross = calculateBcPropertyTransferTax(VICTORIA_PRICE);
const vancouverBaseLoan = VANCOUVER_PRICE * 0.9;
const vancouverCmhcPremium = Math.round(vancouverBaseLoan * 0.031);
const vancouverCmhcPst = Math.round(vancouverCmhcPremium * 0.07);

export const BcCalculatorContext: React.FC = () => (
  <div className="prose prose-stone max-w-none text-ink-muted space-y-4">
    <h3 className="font-serif text-xl text-ink">BC property transfer tax and Vancouver affordability</h3>
    <p>
      British Columbia charges Property Transfer Tax (PTT), not land transfer tax. The rate structure differs
      from Ontario: 1% on the first $200,000, 2% from $200,001 to $3,000,000, and 3% above that. On the
      Vancouver tab at {formatUsd(VANCOUVER_PRICE)}, gross PTT is {formatUsd(vancouverPttGross)} for a repeat
      buyer. Victoria at {formatUsd(VICTORIA_PRICE)} triggers {formatUsd(victoriaPttGross)} before exemptions.
      PTT is a closing-day cash cost separate from your monthly P&amp;I.
    </p>
    <p>
      First-time buyers may qualify for exemptions when the fair market value is under $860,000 (registered on
      or after April 1, 2024). Purchases at $500,000 or below receive a full exemption. Between $500,001 and
      $835,000, a flat {formatUsd(BC_FTHB_FLAT_EXEMPTION)} credit applies. On {formatUsd(VANCOUVER_PRICE)}, a
      qualifying first-time buyer pays about {formatUsd(vancouverPttFthb)} in net PTT after the sliding-scale
      exemption phases out. BC also offers a separate newly built home exemption for qualifying purchases of
      new construction up to $750,000, which can eliminate PTT entirely on eligible new builds.
    </p>
    <p>
      CMHC insurance works the same as in other provinces, but BC charges 7% provincial sales tax on the premium
      in cash at closing. On the Vancouver insured path, a {formatUsd(vancouverCmhcPremium)} premium adds about{' '}
      {formatUsd(vancouverCmhcPst)} in PST. Vancouver&apos;s higher purchase prices and lower property tax mill
      rates (modeled at 0.3%) push monthly P&amp;I higher than Victoria despite similar contract rates. Use the
      Compare tab to see how the insured loan balance and PTT stack up across both cities.
    </p>
    <p className="text-xs text-ink-muted border-l-4 border-stone-200 pl-3">
      Sources: Government of British Columbia, &quot;Property Transfer Tax,&quot; accessed June 2026. URL:{' '}
      <a
        href="https://www2.gov.bc.ca/gov/content/taxes/property-taxes/property-transfer-tax"
        className="text-gold-700 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www2.gov.bc.ca/gov/content/taxes/property-taxes/property-transfer-tax
      </a>
      . Canada Mortgage and Housing Corporation, &quot;CMHC mortgage loan insurance costs,&quot; accessed June 2026. URL:{' '}
      <a
        href="https://www.cmhc-schl.gc.ca/consumers/home-buying/mortgage-loan-insurance-for-consumers/cmhc-mortgage-loan-insurance-cost"
        className="text-gold-700 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.cmhc-schl.gc.ca/consumers/home-buying/mortgage-loan-insurance-for-consumers/cmhc-mortgage-loan-insurance-cost
      </a>
      . See also our{' '}
      <Link to="/learn/guides/mortgage-calculator-bc-guide" className="text-gold-700 hover:underline">
        BC mortgage guide
      </Link>
      .
    </p>
  </div>
);
